// MIDAS V3 — Artifact dock pane (Phase 3)
//
// Spec: MIDAS_V3_PHASE3_ARTIFACT_DOCK_SPEC.md §2
// Handoff: artifact-pane.jsx `ArtifactPane` composition.
//
// Renders the v3 chrome (header tab strip + footer toolbar) around v2's
// existing viewers. Per the wrapper-no-restyle invariant, v2 viewer internals
// are byte-untouched — we only wrap them in a v3 chrome scope so background,
// padding, and surrounding spacing match the v3 palette.
//
// Viewer routing follows v2 `MidasArtifactPanelV2.ArtifactContent` exactly:
//   image           → MidasArtifactImageViewerV2
//   diagram_overlay → MidasArtifactDiagramViewerV2 (with overlaysVisible)
//   action_form     → MidasArtifactActionFormV2 (lazy)
//   table           → MidasArtifactTableRendererV2
//   machine_card    → MidasArtifactMachineCardV3 (ME-CONTRACT renderer, SP-2)
//   sr_summary      → MidasArtifactSRSummaryV2
//   sr_detail       → MidasArtifactSRDetailV2
//
// Empty state: when no artifact tabs are open, the dock should not render
// (the parent layout handles mount/unmount). We still defend against an
// empty active tab here with a quiet placeholder.
//
// Fullscreen mode: when `fullscreen` is true, the pane drops its left border
// and stretches to fill the viewport. Header is hidden (the fullscreen
// chrome owns the breadcrumb + back button + describe + download). The
// footer has been removed entirely (R2 sub-task 1) — fullscreen exits via
// the FullscreenArtifactV3 top-bar back button or Esc.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { Component, ErrorInfo, ReactNode, Suspense, lazy, useCallback } from "react";
import { FileWarning, Layers } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ArtifactTab } from "../../../midasPageModels";
// DIAG-OVERLAY-B: the dock is the bridge between the comp-ref pending-flash
// slice state and the v2 diagram viewer's additive flash props — exactly the
// role it already plays for overlayVisibility.
import { consumeCompFlash } from "../../../midasV3Slice";
import { diagramOverlayPageOf } from "../../../utils/compRefFlash";
import ArtifactHeaderV3 from "./ArtifactHeaderV3";
// R2 sub-task 1: ArtifactFooterV3 import removed. The footer was entirely
// dropped — v2 viewers expose their own zoom + overlay-eye toolbars and the
// only unique control (fullscreen toggle) now lives on ArtifactHeaderV3.
// ArtifactFooterV3.tsx is kept on disk for one deploy cycle as dead code
// in case of rollback; a follow-up commit deletes the orphan.

// v2 viewer imports — wrapped, not modified (sole exception: a single
// additive `overlaysVisible?` prop on MidasArtifactDiagramViewerV2 per
// MIDAS-V3-P3 spec §73).
import MidasArtifactImageViewerV2 from "../../v2/MidasArtifactImageViewerV2";
import MidasArtifactDiagramViewerV2 from "../../v2/MidasArtifactDiagramViewerV2";
import MidasArtifactTableRendererV2 from "../../v2/MidasArtifactTableRendererV2";
import MidasArtifactSRSummaryV2 from "../../v2/MidasArtifactSRSummaryV2";
// ME-MACHINEID-RENDER SP-2: machine_card now routes to the new ME-CONTRACT
// renderer (tabs[]/figures[]/freshness/sources[]). The obsolete flat viewer
// (`MidasArtifactMachineCardV2`, which read `data.machine_id` → "Machine not
// found" under the new shape) was retired to v2/_dead-code/.
import MidasArtifactMachineCardV3 from "./MidasArtifactMachineCardV3";
// ME-MACHINEID-RENDER SP-4: machine_evidence (question-focused snapshot card:
// hero + supporting[] + deep-link into a machine_card tab). Registered by SP-3;
// this arm adds the viewer. Emit gate OFF — exercised via the SP-6 fixtures.
import MidasArtifactMachineEvidenceV3 from "./MidasArtifactMachineEvidenceV3";
import MidasArtifactSRDetailV2 from "../../v2/MidasArtifactSRDetailV2";
// NOTE (MIDAS-V3-P6 hotfix, 2026-05-18): the Radix `TooltipProvider` that
// previously lived around the active-viewer subtree here has been HOISTED
// to MidasV3Layout (matches v2 MidasV2Layout's layout-root pattern). That
// provider now covers the rail (theme switcher), chat outlet, dock, new
// chat dialog, and fullscreen overlay. We deliberately don't re-wrap here
// — nested providers work but are noise, and one source of truth is
// easier to reason about. If MidasArtifactPaneV3 is ever rendered outside
// MidasV3Layout, add a provider at that mount point.

const MidasArtifactActionFormV2 = lazy(
  () => import("../../v2/MidasArtifactActionFormV2")
);

// ───────────────────────────────────────────────────────────────────
// Error boundary — mirrors v2 ArtifactErrorBoundary, in v3 chrome.
// ───────────────────────────────────────────────────────────────────

interface ArtifactErrorBoundaryProps {
  tab: ArtifactTab;
  translate: (key: string, options?: Record<string, unknown>) => string;
  children: ReactNode;
}

interface ArtifactErrorBoundaryState {
  hasError: boolean;
}

class ArtifactErrorBoundary extends Component<
  ArtifactErrorBoundaryProps,
  ArtifactErrorBoundaryState
> {
  state: ArtifactErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(_: Error): ArtifactErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.warn(
      "[MidasArtifactPaneV3] Render error:",
      error.message,
      info.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "40px 24px",
            color: "var(--muted)",
            fontFamily: "var(--font-sans)",
          }}
        >
          <FileWarning size={44} strokeWidth={1.4} style={{ opacity: 0.55 }} />
          <div
            style={{
              maxWidth: 240,
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink-2)",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {this.props.tab.artifact.title}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--muted)",
              textAlign: "center",
            }}
          >
            {this.props.translate("midas.artifact.contentNotLoaded", {
              defaultValue: "Content not loaded",
            })}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ───────────────────────────────────────────────────────────────────
// Viewer router — reads slice for diagram overlay visibility so the v3
// footer's eye-toggle (which dispatches setOverlayVisibility) reaches the
// v2 diagram viewer via its additive `overlaysVisible` prop.
// ───────────────────────────────────────────────────────────────────

interface ArtifactContentV3Props {
  tab: ArtifactTab;
}

const ArtifactContentV3: React.FC<ArtifactContentV3Props> = ({ tab }) => {
  const dispatch = useAppDispatch();
  const overlayMap = useAppSelector((s) => s.midasV3.overlayVisibility);
  const overlaysVisible =
    overlayMap[tab.id] === undefined ? true : overlayMap[tab.id];

  // ── DIAG-OVERLAY-B: pending component flash for THIS sheet ──
  //
  // Only the ACTIVE tab is ever mounted here, which is precisely what makes
  // one mechanism cover both spec rule (a) (the sheet is already on screen →
  // the viewer is mounted → it flashes at once) and rule (e) (the sheet was a
  // background tab → the viewer mounts on reveal, fetches hotspots, and only
  // then flashes). No separate "reveal" code path exists, by design.
  //
  // The viewer LATCHES the flash into local state and owns its lifetime, so
  // consuming the pending the moment it arms is safe — and is what stops a
  // second visit to the same tab from re-flashing (acceptance §8.7).
  const tabPage = diagramOverlayPageOf(tab.artifact);
  const pendingFlash = useAppSelector((s) =>
    tabPage === undefined ? undefined : s.midasV3.pendingCompFlash[tabPage]
  );
  const handleFlashArmed = useCallback(() => {
    if (tabPage === undefined) return;
    dispatch(consumeCompFlash({ page: tabPage }));
  }, [dispatch, tabPage]);

  switch (tab.artifact.type) {
    case "diagram_overlay":
      return (
        <MidasArtifactDiagramViewerV2
          key={tab.id}
          tab={tab}
          overlaysVisible={overlaysVisible}
          flashComponentId={pendingFlash?.component}
          flashNonce={pendingFlash?.nonce}
          onFlashArmed={handleFlashArmed}
          // R5 sub-task 2a: suppress the v2 viewer's internal bottom-toolbar
          // eye toggle. v3 owns the eye in the dock + fullscreen headers; the
          // internal v2 eye would be visually redundant and the click would
          // only flip the v2's local state (which the overlaysVisible prop
          // then overrides anyway — so the eye flips icon but doesn't actually
          // toggle the SVG hotspot overlays).
          hideInternalEyeToggle
        />
      );
    case "action_form":
      return (
        <Suspense
          fallback={
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                padding: 32,
                color: "var(--muted)",
                fontFamily: "var(--font-sans)",
                fontSize: 13,
              }}
            >
              …
            </div>
          }
        >
          <MidasArtifactActionFormV2 key={tab.id} tab={tab} />
        </Suspense>
      );
    case "table":
      return <MidasArtifactTableRendererV2 key={tab.id} tab={tab} />;
    case "machine_card":
      return <MidasArtifactMachineCardV3 key={tab.id} tab={tab} />;
    case "machine_evidence":
      return <MidasArtifactMachineEvidenceV3 key={tab.id} tab={tab} />;
    case "sr_summary":
      return <MidasArtifactSRSummaryV2 key={tab.id} tab={tab} />;
    case "sr_detail":
      return <MidasArtifactSRDetailV2 key={tab.id} tab={tab} />;
    case "platform_file":
    case "image":
    default:
      return <MidasArtifactImageViewerV2 key={tab.id} tab={tab} />;
  }
};

// ───────────────────────────────────────────────────────────────────
// MidasArtifactPaneV3
// ───────────────────────────────────────────────────────────────────

export interface MidasArtifactPaneV3Props {
  /** When true the pane renders as the inner body of a fullscreen overlay
   *  (no left border, header suppressed — the fullscreen chrome handles
   *  back/breadcrumb/describe/download). */
  fullscreen?: boolean;
}

const MidasArtifactPaneV3: React.FC<MidasArtifactPaneV3Props> = ({
  fullscreen = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const tabs = useAppSelector((s) => s.midasPage.artifactTabs);
  const activeTabId = useAppSelector((s) => s.midasPage.activeArtifactTabId);
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        background: "var(--bg)",
        borderLeft: fullscreen ? "none" : "1px solid var(--border)",
      }}
    >
      {!fullscreen && <ArtifactHeaderV3 fullscreen={false} />}

      {/* Body */}
      <div
        className="midas-v3-artifact-body"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: fullscreen ? "20px 32px" : 12,
          overflow: "hidden",
          background: "var(--bg)",
        }}
      >
        {activeTab ? (
          <div
            style={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 6,
              border: "1px solid var(--border)",
              background: "var(--card)",
              boxShadow: "0 1px 2px rgba(40, 30, 15, 0.04)",
            }}
          >
            {/* TooltipProvider is supplied by MidasV3Layout (P6 hotfix). */}
            <ArtifactErrorBoundary tab={activeTab} translate={translate as never}>
              <ArtifactContentV3 tab={activeTab} />
            </ArtifactErrorBoundary>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: "40px 24px",
              color: "var(--muted)",
              fontFamily: "var(--font-sans)",
            }}
          >
            <Layers size={36} strokeWidth={1.4} style={{ opacity: 0.5 }} />
            <div
              style={{
                maxWidth: 280,
                textAlign: "center",
                fontSize: 13,
                lineHeight: 1.55,
              }}
            >
              {translate("midas.emptyState.noArtifacts", {
                defaultValue: "No artifacts open yet.",
              }) as string}
            </div>
          </div>
        )}
      </div>

      {/* R2 sub-task 1: ArtifactFooterV3 render removed. */}
    </div>
  );
};

export default MidasArtifactPaneV3;
